'use client'

import React, { ReactElement } from 'react'
import classNames from 'classnames'
import { onMobile } from '@/lib/utils/utils'
import FilterLabel from '@/components/ui/wizard/filter/FilterLabel'
import { LocationFilterElement } from '../../../../domain-config/domain-config'
import Checkbox from '@/components/common/Checkbox'
import { useTranslations } from 'next-intl'

interface SelectOptionsProps {
  selectedLocationFilter: LocationFilterElement[]
  setLocationFilterLocalState: (l: LocationFilterElement[]) => void
  options: Record<string, LocationFilterElement[]>
  inDropDown?: boolean
}

function SelectOption(props: {
  e: LocationFilterElement
  checked: boolean
  id: string
  onChange: () => void
}): ReactElement {
  const t = useTranslations('SearchPage.Props')

  return (
    <div
      className={classNames(
        'flex flex-row-reverse items-center justify-between gap-2 pb-3 pl-1 md:flex-row md:justify-start md:pb-0',
        props.e.separated && 'mt-6',
      )}
    >
      <Checkbox
        checked={props.checked}
        id={props.id}
        onChange={props.onChange}
        className="h-5 w-5"
      />
      <label
        className="form-check-label inline-block text-xl text-gray-800 md:text-lg"
        htmlFor={props.id}
        onClick={(e) => {
          e.preventDefault()
          props.onChange()
        }}
      >
        {t(props.e.label || props.e.value.toString())}
      </label>
    </div>
  )
}

export default function SelectOptions(props: SelectOptionsProps): ReactElement {
  function locationFilterActive(locationFilterElement: LocationFilterElement): boolean {
    return !!props.selectedLocationFilter.find(
      (filterOption) =>
        filterOption.type === locationFilterElement.type &&
        filterOption.value === locationFilterElement.value,
    )
  }

  function toggleLocationFilter(locationFilterElement: LocationFilterElement): void {
    if (locationFilterActive(locationFilterElement)) {
      props.setLocationFilterLocalState(
        props.selectedLocationFilter.filter(
          (l) => l.value !== locationFilterElement.value || l.type !== locationFilterElement.type,
        ),
      )
    } else {
      props.setLocationFilterLocalState([...props.selectedLocationFilter, locationFilterElement])
    }
  }

  function getId(label: string, e: LocationFilterElement): string {
    return `select-option-${props.inDropDown?.toString() || ''}-${e.value}-${e.type}`
  }

  return (
    <>
      {Object.entries(props.options).map(([label, locationFilterElements]) => (
        <div className="mb-6 last:lg:mb-28" key={label}>
          <FilterLabel label={`SearchPage.FilterLabel.${label}`} />
          <div className="flex flex-row">
            <div className="flex grow basis-0 flex-col">
              {locationFilterElements
                .filter((e, i) => (i + 1) % 2 || onMobile())
                .map((e) => (
                  <SelectOption
                    key={e.value}
                    e={e}
                    checked={locationFilterActive(e)}
                    id={getId(label, e)}
                    onChange={() => toggleLocationFilter(e)}
                  />
                ))}
            </div>
            {!onMobile() && (
              <div className="flex grow basis-0 flex-col">
                {locationFilterElements
                  .filter((e, i) => i % 2)
                  .map((e) => (
                    <SelectOption
                      key={e.value}
                      e={e}
                      checked={locationFilterActive(e)}
                      id={getId(label, e)}
                      onChange={() => toggleLocationFilter(e)}
                    />
                  ))}
              </div>
            )}
          </div>
        </div>
      ))}
    </>
  )
}
